/**
 * playbooks-inflection-wiring (2026-09-17) — the two cache patchers for the
 * new Inflection identity fields (flow id on non-Gong channels, CC /
 * Reply-to), modelled directly on `sender-decision.ts#applySequenceSenderToCache`:
 * the SAME two `setQueryData` patches (library row + sequence detail) followed
 * by the same two `invalidateQueries` safety nets, so a save's own popover
 * updates both the Config row and the play-flow header on the same tick as
 * the write, without waiting for a refetch.
 *
 * Pure of React (no hooks) for the same reason `sender-decision.ts` is — a
 * plain module function `bun test --isolate` can target directly.
 */
import type { QueryClient } from '@tanstack/react-query';

import { libraryQueryKey, sequenceDetailQueryKey } from './query-keys';
import type { PlaybooksLibraryResponse, PlaybooksSequenceDetailResponse } from './schemas';

/** Patches `sequences[].flowId` (library) and `sequence.flowId` (detail) after a flow-id save. */
export function applySequenceFlowIdToCache(
  queryClient: QueryClient,
  sequenceId: string,
  flowId: string,
): void {
  queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      sequences: prev.sequences.map((seq) => (seq.id === sequenceId ? { ...seq, flowId } : seq)),
    };
  });
  queryClient.setQueryData<PlaybooksSequenceDetailResponse>(
    sequenceDetailQueryKey(sequenceId),
    (prev) => (prev ? { ...prev, sequence: { ...prev.sequence, flowId } } : prev),
  );
  void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
}

/** Patches `sequences[].cc`/`.replyTo` (library) and `sequence.cc`/`.replyTo` (detail) after an Inflection-fields save. */
export function applySequenceInflectionFieldsToCache(
  queryClient: QueryClient,
  sequenceId: string,
  fields: { cc: string | null; replyTo: string | null },
): void {
  queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      sequences: prev.sequences.map((seq) => (seq.id === sequenceId ? { ...seq, ...fields } : seq)),
    };
  });
  queryClient.setQueryData<PlaybooksSequenceDetailResponse>(
    sequenceDetailQueryKey(sequenceId),
    (prev) => (prev ? { ...prev, sequence: { ...prev.sequence, ...fields } } : prev),
  );
  void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
}
